/* ==========================================================================
   Nodus theme behaviors

   - Mobile drawer X-close button: routes the click to the main drawer
     <summary> so Dawn's <header-drawer> closeMenuDrawer runs in full
     (restores body scroll, clears focus trap, resets state).
   - Accordion aria-expanded sync: Dawn's drawer JS only sets aria-expanded
     to "true" when a nested summary is clicked, never back to "false".
     Native <details> toggling drives the true open state, so we mirror it.
   - Desktop mega-menu hover intent: opens on hover with a tiny intent delay
     and closes after a 300ms safety delay so the user can travel between
     the trigger and the panel without the menu collapsing under them.
     Click on the summary still toggles natively (keyboard accessibility).
   ========================================================================== */

(function () {
  'use strict';

  function handleDrawerClose(event) {
    var closeBtn = event.target && event.target.closest
      ? event.target.closest('[data-nodus-drawer-close]')
      : null;
    if (!closeBtn) return;

    event.preventDefault();
    event.stopPropagation();

    var drawer = closeBtn.closest('header-drawer');
    if (!drawer) return;

    var summary = drawer.querySelector(':scope > details > summary');
    if (summary) summary.click();
  }

  function syncAccordionState(event) {
    var details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.closest('.nodus-drawer')) return;

    var summary = details.querySelector(':scope > summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------------
     Desktop mega-menu hover intent
     ------------------------------------------------------------------------ */

  var MEGA_OPEN_DELAY = 90;   // ms — premium intent buffer; quick enough to feel responsive but unhurried
  var MEGA_CLOSE_DELAY = 320; // ms — graceful dismiss matches the panel's 360ms transform-out

  function isHoverCapable() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function closeAllMegaMenusExcept(keepDetails) {
    var openMenus = document.querySelectorAll('details.mega-menu[open]');
    for (var i = 0; i < openMenus.length; i++) {
      var d = openMenus[i];
      if (d === keepDetails) continue;
      d.removeAttribute('open');
      var s = d.querySelector(':scope > summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    }
  }

  function bindMegaMenu(menuEl) {
    if (menuEl.__nodusHoverBound) return;
    menuEl.__nodusHoverBound = true;

    var details = menuEl.querySelector(':scope > details.mega-menu');
    if (!details) return;
    var summary = details.querySelector(':scope > summary');

    var openTimer = null;
    var closeTimer = null;

    function clearOpen() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
    }
    function clearClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    function openMenu() {
      clearOpen();
      clearClose();
      if (details.hasAttribute('open')) return;
      closeAllMegaMenusExcept(details);
      details.setAttribute('open', '');
      if (summary) summary.setAttribute('aria-expanded', 'true');
    }

    function closeMenu() {
      clearClose();
      if (!details.hasAttribute('open')) return;
      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }

    menuEl.addEventListener('mouseenter', function () {
      if (!isHoverCapable()) return;
      clearClose();
      if (details.hasAttribute('open')) return;
      clearOpen();
      openTimer = setTimeout(openMenu, MEGA_OPEN_DELAY);
    });

    menuEl.addEventListener('mouseleave', function () {
      if (!isHoverCapable()) return;
      clearOpen();
      if (!details.hasAttribute('open')) return;
      clearClose();
      closeTimer = setTimeout(closeMenu, MEGA_CLOSE_DELAY);
    });

    /* Native click on summary still toggles via the browser; sync aria
       afterwards so screen readers reflect reality (Dawn does this only
       on click, not on programmatic open). */
    if (summary) {
      summary.addEventListener('click', function () {
        // Defer until after the native toggle has applied.
        setTimeout(function () {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          // If the user opened it by click, cancel any pending hover-close.
          if (details.open) clearClose();
        }, 0);
      });
    }
  }

  function initMegaMenus() {
    var menus = document.querySelectorAll('header-menu');
    for (var i = 0; i < menus.length; i++) bindMegaMenu(menus[i]);
  }

  /* ------------------------------------------------------------------------
     Size guide modal — inject raw HTML from metafield JSON
     (multi_line_text_field values are escaped by Liquid; JSON + innerHTML
     preserves the table markup from custom.beden_tablosu)
     ------------------------------------------------------------------------ */

  function hydrateSizeGuides(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var modals = scope.querySelectorAll
      ? scope.querySelectorAll('modal-dialog.size-guide-modal')
      : [];

    modals.forEach(function (modal) {
      var target = modal.querySelector('[data-size-guide-target]');
      var source = modal.querySelector('[data-size-guide-source]');
      if (!target || !source || target.dataset.hydrated === 'true') return;

      try {
        var html = JSON.parse(source.textContent);
        if (typeof html === 'string' && html.trim().length) {
          target.innerHTML = html;
          target.dataset.hydrated = 'true';
        }
      } catch (error) {
        console.error('Size guide HTML parse failed', error);
      }
    });
  }

  function initSizeGuides() {
    hydrateSizeGuides(document);
  }

  /* ------------------------------------------------------------------------
     Search drawer — reset state on close
     ------------------------------------------------------------------------ */

  function bindSearchDrawer(component) {
    if (component.__nodusSearchBound) return;
    component.__nodusSearchBound = true;

    var details = component.closest('.header__search')?.querySelector('details');

    if (details) {
      details.addEventListener('toggle', function () {
        if (details.open) {
          if (typeof component.ensureReady === 'function') {
            component.ensureReady();
          }
          return;
        }
        if (typeof component.close === 'function') {
          component.close(true);
        }
      });
    }
  }

  function initSearchDrawers() {
    var drawers = document.querySelectorAll('predictive-search.nodus-search__shell');
    for (var i = 0; i < drawers.length; i++) {
      bindSearchDrawer(drawers[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initMegaMenus();
      initSizeGuides();
      initSearchDrawers();
    });
  } else {
    initMegaMenus();
    initSizeGuides();
    initSearchDrawers();
  }

  document.addEventListener('shopify:section:load', function (event) {
    hydrateSizeGuides(event.target);
    var loaded = event.target.querySelectorAll
      ? event.target.querySelectorAll('predictive-search.nodus-search__shell')
      : [];
    for (var i = 0; i < loaded.length; i++) {
      bindSearchDrawer(loaded[i]);
    }
  });

  document.addEventListener('click', handleDrawerClose, true);
  document.addEventListener('toggle', syncAccordionState, true);
})();
